import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Email, Lock, Storefront, ArrowBack } from "@mui/icons-material";
import { StyledTextField, StyledButton, TermsFooter } from "../common";
import { useNavigate } from "react-router-dom";

interface LoginFormProps {
  /** Current credentials state */
  credentials: {
    username: string;
    password: string;
  };
  /** Validation errors for each field */
  validationErrors: {
    username?: string;
    password?: string;
  };
  /** Whether password is visible */
  showPassword: boolean;
  /** Whether form is submitting */
  isLoading: boolean;
  /** Handler for input changes */
  onInputChange: (
    field: "username" | "password",
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Handler for password visibility toggle */
  onTogglePassword: () => void;
  /** Handler for form submission */
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

/**
 * LoginForm Component
 *
 * Login form with username, password fields, and submit button.
 * Includes validation error display and loading state.
 *
 * Features:
 * - Username field with email validation
 * - Password field with visibility toggle
 * - Submit button with loading spinner
 * - Terms and privacy policy links
 * - Error alert display
 */
const LoginForm: React.FC<LoginFormProps> = ({
  credentials,
  validationErrors,
  showPassword,
  isLoading,
  onInputChange,
  onTogglePassword,
  onSubmit,
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        zIndex: 2,
      }}
    >
      {/* Mobile Brand Header (visible on mobile only) */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pt: 5,
          pb: 3,
          px: 3,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
          zIndex: 3,
          minHeight: "120px",
          // Gold accent stripe at bottom
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #d4af37 0%, #FFD700 100%)",
            opacity: 0.85,
          },
        }}
      >
        {/* Back Button (top-left) */}
        <IconButton
          onClick={() => navigate("/")}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            color: "white",
            bgcolor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.2)",
            },
          }}
          aria-label="Back to landing page"
        >
          <ArrowBack />
        </IconButton>

        {/* Logo Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            border: "2px solid rgba(255, 215, 0, 0.3)",
            boxShadow: "0 8px 24px rgba(26, 35, 126, 0.25)",
          }}
        >
          <Storefront
            sx={{
              fontSize: 40,
              color: "white",
            }}
          />
        </Box>

        {/* Brand Name */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "white",
            fontSize: "1.5rem",
            letterSpacing: "0.02em",
            mb: 0.5,
            textShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <Box component="span" sx={{ color: "white" }}>
            Mall
          </Box>
          <Box component="span" sx={{ color: "#FFD700" }}>
            In
          </Box>
          <Box component="span" sx={{ color: "white" }}>
            Hand
          </Box>
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "rgba(255, 255, 255, 0.85)",
            fontSize: "0.875rem",
            fontWeight: 500,
            textShadow: "0 1px 4px rgba(0,0,0,0.1)",
          }}
        >
          Admin Dashboard
        </Typography>
      </Box>

      {/* Form Container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", md: "center" },
          px: { xs: 3, sm: 4, md: 5 },
          pt: { xs: "148px", md: 0 },
          pb: { xs: 4, md: 0 },
          maxWidth: 440,
          mx: "auto",
          width: "100%",
        }}
      >
        {/* Form Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 3.5 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.625rem", sm: "1.875rem", md: "2.125rem" },
              letterSpacing: "-0.01em",
            }}
          >
            Admin Dashboard
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
              fontWeight: 500,
              px: { xs: 2, md: 0 },
            }}
          >
            Sign in to access your dashboard
          </Typography>
        </Box>

        {/* Login Form */}
        <Box component="form" onSubmit={onSubmit} noValidate>
          {/* Username Field */}
          <StyledTextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username or Email"
            name="username"
            autoComplete="username"
            autoFocus
            value={credentials.username}
            onChange={onInputChange("username")}
            error={!!validationErrors.username}
            helperText={validationErrors.username}
            disabled={isLoading}
            startIcon={<Email sx={{ color: "primary.main" }} />}
          />

          {/* Password Field */}
          <StyledTextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            id="password"
            autoComplete="current-password"
            value={credentials.password}
            onChange={onInputChange("password")}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            disabled={isLoading}
            isPassword
            showPassword={showPassword}
            onTogglePassword={onTogglePassword}
            startIcon={<Lock sx={{ color: "primary.main" }} />}
          />

          {/* Submit Button */}
          <StyledButton
            type="submit"
            fullWidth
            isLoading={isLoading}
            loadingText="Signing In..."
            aria-label={
              isLoading ? "Signing in, please wait" : "Sign in to dashboard"
            }
            sx={{ mt: 3 }}
          >
            Sign In
          </StyledButton>
        </Box>

        {/* Terms Footer */}
        <TermsFooter />
      </Box>
    </Box>
  );
};

export default React.memo(LoginForm);
